import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export default function PostCheckoutLinker() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const purchased = searchParams.get('purchased') === 'true';
  const planFromUrl = searchParams.get('plan');
  const planFromStorage = localStorage.getItem('pendingStripePurchase');

  const plan = planFromUrl || planFromStorage;
  const hasPendingPurchase = purchased || !!planFromStorage;

  const linkingCalled = useRef(false);
  const [status, setStatus] = useState('idle'); // idle, linking, success, already_linked, not_found, error
  const [message, setMessage] = useState('');

  // If no purchase params (URL or localStorage), redirect immediately to /agents
  useEffect(() => {
    if (!hasPendingPurchase || !plan) {
      navigate('/agents', { replace: true });
    }
  }, [hasPendingPurchase, plan, navigate]);

  // Link the Stripe subscription
  useEffect(() => {
    if (!hasPendingPurchase || !plan) return;
    if (linkingCalled.current) return;
    linkingCalled.current = true;

    const linkStripe = async () => {
      setStatus('linking');
      setMessage('Linking your subscription...');

      try {
        const token = await getToken();
        const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://kalibr-backend.fly.dev';
        const response = await fetch(`${API_BASE_URL}/api/billing/link-stripe-customer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        const result = await response.json();

        if (result.status === 'success') {
          localStorage.removeItem('pendingStripePurchase');
          setStatus('success');
          setMessage(`Successfully activated your ${plan} plan!`);
          setTimeout(() => {
            navigate('/agents?welcome=true', { replace: true });
          }, 2000);
        } else if (result.status === 'already_linked') {
          localStorage.removeItem('pendingStripePurchase');
          setStatus('already_linked');
          setMessage('Your account is already linked to a paid plan!');
          setTimeout(() => {
            navigate('/agents', { replace: true });
          }, 1500);
        } else if (result.status === 'not_found') {
          localStorage.removeItem('pendingStripePurchase');
          setStatus('not_found');
          setMessage('Could not find your Stripe subscription. Please contact support.');
          setTimeout(() => {
            navigate('/settings?link_failed=true', { replace: true });
          }, 4000);
        } else {
          localStorage.removeItem('pendingStripePurchase');
          setStatus('error');
          setMessage('Failed to link subscription. Please contact support.');
          setTimeout(() => {
            navigate('/settings?link_failed=true', { replace: true });
          }, 4000);
        }
      } catch (error) {
        console.error('Failed to link Stripe subscription:', error);
        localStorage.removeItem('pendingStripePurchase');
        setStatus('error');
        setMessage('An error occurred while linking your subscription. Please contact support.');
        setTimeout(() => {
          navigate('/settings?link_failed=true', { replace: true });
        }, 4000);
      }
    };

    linkStripe();
  }, [hasPendingPurchase, plan, getToken, navigate]);

  if (!hasPendingPurchase || !plan) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Setting up your account</h2>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              status === 'linking' || status === 'idle'
                ? 'bg-blue-50 border-blue-200'
                : status === 'success' || status === 'already_linked'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-center gap-3">
              {(status === 'linking' || status === 'idle') && (
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              )}
              {(status === 'success' || status === 'already_linked') && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              {(status === 'error' || status === 'not_found') && (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              <p
                className={`text-sm ${
                  status === 'linking' || status === 'idle'
                    ? 'text-blue-800'
                    : status === 'success' || status === 'already_linked'
                    ? 'text-green-800'
                    : 'text-red-800'
                }`}
              >
                {message || 'Preparing...'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
